/**
 * @file
 * App Configuration
 *
 * This file provides the standard configuration of this webapp
 */

/**
 * App Configuration
 */
export abstract class AppConfig {
  /**
   * Default colors
   */
  static readonly primaryColor = '#ffbb16'
  static readonly secondaryColor = '#b71c1c'
  static readonly tertiaryColor = '#404041'
  static readonly successColor = '#9bff2d'
  static readonly warningColor = '#f2ad09'
  static readonly dangerColor = '#b71c1c'

  /**
   * Filter debounce wait time
   */
  static readonly debounceWaitTime = 1000

  /**
   * Sentinel watch interval (ms)
   */
  static readonly sentinelWatchInterval = 60000

  /**
   * Upload endpoint
   */
  static readonly uploadEndpoint = '/user/upload-url'

  /**
   * First date for year filter
   */
  static readonly firstDate = '2017-01-01T00:00:00-03:00'

  /**
   * First date for excel
   */
  static readonly excelFirstDate = '1899-12-31'

  /**
   * Fields to input at view components
   */
  static readonly fieldsToInput = {
    deliveryPersist: {
      CargaComDanfe: ['danfe', 'nroNotaFiscal', 'dataPrevisao'],
      CargaSemDanfe: [
        'serialNumber',
        'nroNotaFiscal',
        'dataPrevisao',
        'xpedido',
      ],
      ResponsavelCargaEmbarcador: [
        'razaoSocial',
        'nomeFantasia',
        'cnpj',
        'telefone',
      ],
      CargaDestinatario: ['nome', 'cpfCnpj', 'telefone', 'email'],
      ResponsavelCargaTransportador: ['razaoSocial', 'cnpj'],
    },
    manageChecklistEntrega: {
      ChecklistEntrega: [
        'idOcorrenciaExternaAlt',
        'codigoServico',
        'descricao',
      ],
    },
    manageCnpjBulklog: {
      ChecklistEntrega: ['nome', 'cnpj'],
    },
    deliveryEnd: {
      Carga: [
        'dataFinalizacao',
        'dataBaixaSistema',
        'qtdPaleteEntregue',
        'qtdPaleteRetirado',
      ],
    },
    userPersist: {
      User: [
        'cnpj',
        'razaoSocial',
        'nomeFantasia',
        'nomeContato',
        'telefone',
        'email',
      ],
      secondaryUser: ['nome', 'email'],
    },
    driverPersist: {
      MotoristaP1: [
        'nome',
        'rg',
        'celular',
        'email',
        'dataNascimento',
        'telefoneFixo',
      ],
      MotoristaP2: [
        'tipoVeiculo',
        'marcaVeiculo',
        'placaVeiculo',
        'tipoCarroceria',
        'placaCarroceira',
      ],
      MotoristaP3: ['antt', 'nomeIndicador', 'cnh'],
      MotoristaP4: [
        'horaInicio',
        'horaFim',
        'rangeCepInicial',
        'rangeCepFinal',
        'segunda',
        'terca',
        'quarta',
        'quinta',
        'sexta',
        'sabado',
        'domingo',
        'ativo',
      ],
    },
    auditReasonPersist: {
      AuditoriaMotivo: ['nome', 'ativo'],
    },
    occurrenceTypePersist: {
      TipoOcorrencia: ['nomeAlt', 'idOcorrenciaExternaAlt'],
    },
    personTypePersist: {
      TipoPessoaRecebedor: ['nome', 'ativo'],
    },
    occurrenceTypeSucessPersist: {
      TipoOcorrenciaSucesso: ['nomeAlt', 'idOcorrenciaExternaAlt'],
    },
    AlertPersist: {
      Alerta: ['idAlertaPk', 'descricao', 'ativo'],
    },
    HideResponsavelNotasPersist: {
      OcultarNotasResponsavel: ['idOcultarNotasResponsavelPk', 'razaoSocial', 'cnpj', 'ativo'],
    },
    AuditEmail: {
      EmailAuditoria: ['idEmailAuditoriaPk', 'nomeGrupo'],
    },
    RiskAreaPersist: {
      AreaDeRisco: ['cepInicial', 'cepFinal', 'ativo', 'notificarNoApp', 'notificarNoPainel'],
    },
    RespUserPersist: {
      ResponsavelUsuario: ['nome', 'email', 'senha', 'ativo'],
    },
  }

  /**
   * Fields to render at view components
   */
  static readonly fieldsToRender = {
    deliveryList: {
      Carga: [
        'riskArea',
        'actions',
        'actionsTrash',
        'nroNotaFiscal',
        'nroCteAlt',
        'danfe',
        'tentativa',
        'motorista',
        'placaVeiculoAlt',
        'transportador',
        'endereco',
        'dataCriacao',
        'dataBaixaSistema',
        'dataFinalizacao',
        'tempoEntrega',
        'dataPrevisao',
        'slaStatus',
        'statusCarga',
        'event',
        'tipoOcorrencia',
        'nroFotosOcorrencia',
        'nroComprovantes',
        'centroDeCusto',
      ],
    },
    deliveryGet: {
      CargaDestinatario: ['nome', 'cpfCnpj', 'telefone', 'email'],
      CargaResponsavel: ['nome', 'cnpj'],
      CargaConcreto: [
        'descricaoProduto',
        'usina',
        'adicaoAgua',
        'retornoConcreto',
        'hodometroInicial',
        'hodometroFinal',
      ],
      InfoAdicionalCielo: [
        'horaFunc',
        'escopoAtendimento',
        'ramoAtividade',
        'gestorComercial',
        'nomeFantasia',
        'contato',
        'telefoneAdicional',
        'observacao',
        'segmento',
        'ocorrencia',
        'tipoInstalacao',
        'ocorrenciaManutencao',
      ],
      InfoAdicionalCieloSigla: ['sigla'],
      CargaHeader: ['criadoVia', 'criadoPorNomeRazaoSocial', 'finalizadoVia'],
      CargaSession1: [
        'nroNotaFiscal',
        'dataBaixaSistema',
        'statusCarga',
        'event',
        'tipoOcorrencia',
        'nroCte',
      ],
      CargaSession2: [
        'dataEmissaoDanfe',
        'dataPrevisao',
        'slaStatus',
        'dataInicioEntrega',
        'dataCheckIn',
        'dataFinalizacao',
        'cdCorreio',
        'dataPrevisaoAtualizada',
        'observacaoPrevisao',
      ],
      CargaSession2Alt: [
        'dataInicioEntrega',
        'dataCheckIn',
        'horarioInicioDescarga',
        'dataFinalizacao',
        'horarioSaidaObra',
        'horarioChegadaCentral',
        'dataEmissaoDanfe',
        'slaStatus',
        'dataPrevisao',
        'cdCorreio',
        'dataPrevisaoAtualizada',
        'observacaoPrevisao',
      ],
      CargaSession3: [
        'qtdPaleteEntregue',
        'qtdPaleteRetirado',
        'observacao',
        'tipoPessoaRecebedor',
        'listaDeAvarias',
      ],
      CargaSession3Alt1: [
        'qtdPaleteEntregue',
        'qtdPaleteRetirado',
        'observacao',
        'observacaoColeta',
        'tipoPessoaRecebedor',
        'tipoOcorrenciaSucesso',
        'listaDeAvarias',
      ],
      CargaSession3Alt2: [
        'qtdPaleteEntregue',
        'qtdPaleteRetirado',
        'observacao',
        'tipoPessoaRecebedor',
        'tipoOcorrenciaSucesso',
        'listaDeAvarias',
      ],
      CargaSession3Alt3: [
        'qtdPaleteEntregue',
        'qtdPaleteRetirado',
        'observacao',
        'observacaoColeta',
        'tipoPessoaRecebedor',
        'listaDeAvarias',
      ],
      CargaSession4: [
        'tipoVeiculo',
        'placaVeiculo',
        'auditoriaLocalCheckIn',
        'auditoriaLocalEntrega',
        'latitudeCheckIn',
        'latitudeEntrega',
        'longitudeCheckIn',
        'longitudeEntrega',
        'pontoReferenciaEntrega',
      ],
    },

    deliveryGetCielo: {
      ServiceOrder1: [
        'tipoDeRemessa',
        'codeServiceType',
        'dataInicioEntrega',
        'dataPrevisao',
        'dataFinalizacao',
        'status',
        'tipoOcorrenciaSucesso',
      ],
      ServiceOrder2: [
        'merchantID',
        'logicalNumber',
        'referenceCode',
        'businessName',
        'legalEntityName',
      ],
      ServiceOrder3: [
        'address',
        'neighborhood',
        'city',
        'state',
        'addressCode',
      ],
      ServiceOrder4: ['nomeRecebedor', 'documentoRecebedor'],
      ServiceOrder5: ['nomeRecebedor', 'rgRecebedor'],
    },
    deliveryGetCteView: ['nroNotaFiscal', 'dataCriacao', 'embarcadorCpfCnpj', 'embarcadorRazaoSocial', 'destinatario'],
    deliveryGetNotasEmAtrasoListView: ['idCargaPk', 'nroNotaFiscal', 'nroCteRenamedAsCte', 'danfe', 'dataCriacao',
      'dataPrevisao', 'slaDias', 'slaByDiasAtraso', 'diasAtraso'],

    manageDeliveryRegister: {
      Carga: [
        'actions',
        'nroNotaFiscal',
        'nroCteAlt',
        'danfe',
        'tentativa',
        'motorista',
        'transportador',
        'endereco',
        'dataCriacao',
        'dataBaixaSistema',
        'dataFinalizacao',
        'tempoEntrega',
        'dataPrevisao',
        'slaStatus',
        'statusCarga',
        'event',
        'tipoOcorrencia',
        'nroFotosOcorrencia',
        'nroComprovantes',
      ],
    },
    manageDeliveryDriver: {
      Carga: [
        'actions',
        'nroNotaFiscal',
        'nroCteAlt',
        'danfe',
        'tentativa',
        'motorista',
        'endereco',
        'dataCriacao',
        'dataPrevisao',
        'slaStatus',
        'statusCarga',
      ],
      Motorista: [
        'urlImagem',
        'idMotoristaPk',
        'nome',
        'cpf',
        'rg',
        'endereco',
        'celular',
        'dataNascimento',
        'email',
        'placaVeiculo',
        'marcaVeiculo',
        'tipoVeiculo',
        'tipoCarroceria',
        'antt',
      ],
      MotoristaComJornada: [
        'urlImagem',
        'idMotoristaPk',
        'nome',
        'cpf',
        'rg',
        'endereco',
        'celular',
        'dataNascimento',
        'email',
        'placaVeiculo',
        'marcaVeiculo',
        'tipoVeiculo',
        'tipoCarroceria',
        'antt',
        'horaInicio',
        'horaFim',
        'rangeCepInicial',
        'rangeCepFinal',
        'ativo',
      ],
      MotoristaTransbordo: [
        'urlImagem',
        'idMotoristaPk',
        'nome',
        'cpf',
        'transbordoEntrega',
        'transbordoRecebimento',
      ],
    },
    manageAuditReason: {
      AuditoriaMotivo: ['nome', 'ativo'],
    },
    manageOccurrenceType: {
      TipoOcorrencia: ['idOcorrenciaExterna', 'nome', 'ehDevolvida', 'ativo'],
    },
    managePersonType: {
      TipoPessoaRecebedor: ['nome', 'ativo'],
    },

    manageAlert: {
      Alerta: ['descricao', 'ativo'],
    },

    manageOccurrenceTypeSucess: {
      TipoOcorrenciaSucesso: ['idOcorrenciaExterna', 'nome', 'ativo'],
    },
    manageChecklistEntrega: {
      ChecklistEntrega: [
        'codigoServico',
        'descricao',
        'idTipoChecklistEntregaPk',
        'ativo',
        'ehEditavel',
      ],
    },
    manageCnpjBulklog: {
      CnpjBulklog: [
        'idCnpjBulklogPk',
        'idTipoChecklistEntregaPkAlt',
        'cnpj',
        'nome',
        'ativo',
      ],
    },
    manageSecondaryUser: {
      ResponsavelUsuario: ['nome', 'email', 'role'],
    },
    manageDeliveryEnd: {
      Carga: [
        'actions',
        'nroNotaFiscal',
        'nroCteAlt',
        'danfe',
        'tentativa',
        'motorista',
        'endereco',
        'dataCriacao',
        'dataPrevisao',
      ],
    },
    manageUserList: {
      Responsavel: [
        'actions',
        'idResponsavelPk',
        'cnpj',
        'razaoSocial',
        'nomeFantasia',
        'nomeContato',
        'telefone',
        'email',
        'dataVencimento',
      ],
    },
    manageHideResponsavelNotas: {
      OcultarNotasResponsavel: ['razaoSocial', 'cnpj', 'ativo'],
    },
    manageAuditEmail: {
      EmailAuditoria: ['nomeGrupo', 'email', 'idEmailNivelFk', 'ativo'],
    },
    manageAwaitingAuditValidation: {
        AuditValidation: [
            'nroNotaFiscal',
            'nroCte',
            'danfe',
            'entregador',
            'placaVeiculo',
            'transportador',
            'enderecoEntrega',
            'dataHoraCriacao',
            'dataHoraBaixa',
            'dataHoraEntrega',
            'auditoria',
            'statusNotificacaoAuditoria',
        ],
    },
    manageAwaitingAuditCorrection: {
      EmailAuditoriaEnvio: [
        'idCargaFk',
        'nroNotaFiscal',
        'dataCriacao',
        'dataBaixa',
        'nomeGrupo',
        'statusAuditoria',
        'emailNivel',
        'dataEnvio',
      ],
    },
    manageRiskArea: {
      AreaDeRisco: [
        'dataCadastro', 'cepInicial', 'cepFinal', 'notificarNoApp', 'notificarNoPainel', 'ativo',
      ],
    },
    manageRotasSlaView: {
      ConfigNovaSla: ['ufOrigem', 'cidadeOrigem', 'ufDestino', 'cidadeDestino', 'sla'],
      ConfigNovaSlaCalendarizacao: ['cnpjEmbarcador', 'cnpjTransportador', 'ufDestino',
        'cidadeDestino', 'sla', 'calendarizacao'],
    },
    manageSlaFeriados: {
      SlaFeriado: ['date', 'desc'],
    },
    manageRespUser: {
      ResponsavelUsuario: ['nome', 'email', 'tipoResponsavelUsuario', 'filial', 'ativo'],
    },

    reportsDeliveryVehicle: {
      DeliveryVehicleResponse: [
        'nome',
        'qtdSemOcorrencia',
        'qtdComOcorrencia',
        'qtdDevolvidas',
        'qtdTotal',
      ],
    },
    reportsIRouteIndicator: {
      ReportsIRouteIndicatorResponse: [
        'nroNotaFiscal',
        'nomeTransportador',
        'enderecoEntrega',
        'dataCriacao',
        'dataBaixa',
        'dataEntrega',
        'tempoEntrega',
        'prevEntrega',
      ],
    },
    reportsDeliveryDriver: {
      deliveryDriverResponse: [
        'nome',
        'documento',
        'placa',
        'qtdSemOcorrencia',
        'qtdComOcorrencia',
        'qtdDevolvidas',
        'qtdTotal',
      ],
    },
    reportsDeliverySla: {
      DeliverySlaResponse: [
        'nome',
        'documento',
        'dentroSLA',
        'porcentagemDentroSLA',
        'foraSLA',
        'porcentagemForaSLA',
        'naoInformadoSLA',
      ],
    },
    reportsDeliveryShipper: {
      DeliveryShipperResponse: [
        'nome',
        'documento',
        'qtdSemOcorrencia',
        'qtdComOcorrencia',
        'qtdDevolvidas',
        'qtdTotal',
      ],
    },
    reportsDeliveryReceiver: {
      DeliveryReceiverResponse: [
        'nome',
        'documento',
        'qtdSemOcorrencia',
        'qtdComOcorrencia',
        'qtdDevolvidas',
        'qtdTotal',
      ],
    },
    reportsDeliveryCanceled: {
      DeliveryCanceledResponse: ['nome', 'nfCarga', 'nroCteAlt', 'danfeCarga', 'data', 'justificativa'],
    },
    reportsAlertHistory: {
      DeliveryAlertHistoryResponse: [
        'mensagem',
        'documento',
        'motorista',
        'data',
      ],
    },
    reportsSatisfactionSurveyHistory: {
      DeliverySatisfactionSurveyResponse: [
        'documento',
        'danfe',
        'xpedido',
        'nomeRecebedor',
        'documentoRecebedor',
        'razaoSocial',
        'nomeMotorista',
        'cpfMotorista',
        'pergunta1',
        'notaPergunta1',
        'pergunta2',
        'notaPergunta2',
        'imgUrl',
        'data',
      ],
    },
    reportsDeliveryUf: {
      DeliveryUfResponse: [
        'nome',
        'qtdSemOcorrencia',
        'qtdComOcorrencia',
        'qtdDevolvidas',
        'qtdTotal',
      ],
    },
    reportsDeliveryPeriod: {
      DeliveryperiodResponse: [
        'mes',
        'qtdSemOcorrencia',
        'qtdComOcorrencia',
        'qtdDevolvidas',
        'qtdTotal',
      ],
    },
    reportsDeliveryCheckList: {
      Carga: [
        'idCargaPk',
        'nroNotaFiscal',
        'dataCriacao',
        'dataInicioEntrega',
        'dataCheckIn',
        'dataPrevisao',
        'dataBaixaSistema',
        'statusCarga',
        'motorista',
        'rgRecebedorAlt',
        'placaVeiculoAlt',
        'tipoVeiculoAlt',
        'documentoRecebedorAlt',
        'destinatario',
        'destinatarioCpfCnpj',
        'destinatarioEndereco',
        'transportadorRazaoSocial',
        'transportadorCpfCnpj',
        'embarcadorRazaoSocial',
        'embarcadorCpfCnpj',
      ],
    },
    reportsDeliveryIssues: {
      Carga: [
        'idCargaPk',
        'nroNotaFiscal',
        'motorista',
        'endereco',
        'dataCriacao',
        'tempoEntrega',
        'dataPrevisao',
        'statusCarga',
        'tipoOcorrencia',
        'nroFotosOcorrencia',
        'motoristaCel',
        'destinatario',
        'destinatarioTelefoneAlt',
      ],
    },
    reportsDeliveryMovement: {
      Carga: [
        'nroNotaFiscal',
        'danfe',
        'tentativa',
        'motorista',
        'endereco',
        'dataCriacao',
        'dataBaixaSistema',
        'dataFinalizacao',
        'tempoEntrega',
        'dataPrevisao',
        'slaStatus',
        'statusCarga',
        'event',
        'tipoOcorrencia',
        'nroFotosOcorrencia',
        'nroComprovantes',
      ],
    },
    reportsDeliveryAudit: {
      DeliveryAuditResponse: [
        'nome',
        'documento',
        'checkinConforme',
        'checkinNaoConforme',
        'checkinNaoInformado',
        'entregaConforme',
        'entregaNaoConforme',
        'entregaNaoInformado',
      ],
    },
    reportsDeliveryMonth: {
      DeliveryMonthResponse: [
        'data',
        'qtdSemOcorrencia',
        'qtdComOcorrencia',
        'qtdDevolvidas',
        'qtdTotal',
      ],
    },
    reportsDeliveryTracking: {
      DeliveryTrackingResponse: ['nome', 'primeiraData', 'ultimaData'],
    },
    reportsDeliverySacPublic: {
      Carga: [
        'nroNotaFiscal',
        'dataInclusao',
        'embarcadorCpfCnpjAlt',
        'embarcadorRazaoSocialAlt',
        'nomeDestinatario',
      ],
    },
    reportsDeliverySacAgcoPublic: {
      Carga: [
        'nroNotaFiscal',
        'dataCriacao',
        'embarcadorCpfCnpjAlt',
        'embarcadorRazaoSocialAlt',
        'nomeDestinatario',
        'dataPrevisao',
        'statusCarga',
      ],
      CargaObservacao: ['observacaoEntrega'],
    },
    reportsDeliverySacBostonPublic: {
      Carga: [
        'nroNotaFiscal',
        'dataCriacao',
        'embarcadorCpfCnpjAlt',
        'embarcadorRazaoSocialAlt',
        'nomeDestinatario',
      ],
    },
    reportsDeliverySac: {
      Carga: [
        'danfe',
        'nroNotaFiscal',
        'nroCteAlt',
        'cpfCnpjDestinatario',
        'nomeDestinatario',
      ],
      CargaSession1: ['nroNotaFiscal', 'danfe', 'cte'],
      CargaSessionPrivate: ['nroNotaFiscal', 'dataPrevisao',  'nroCte', 'danfe'],
      CargaSessionPrivateAlt: [
        'nroNotaFiscal',
        'dataPrevisao',
        'nroCte',
        'dataPrevisaoAtualizadaPrivate',
        'observacaoPrevisaoPrivate',
        'danfe',
      ],
      CargaSessionPrivateAltIvecoCnh: [
        'nroNotaFiscal',
        'dataPrevisao',
        'nroCte',
        'dataPrevisaoAtualizadaPrivate',
        'observacaoPrevisaoPrivate',
        'danfe',
        'lastUpdate',
      ],
      CargaSessionPublic: [
        'nroNotaFiscal',
        'danfe',
        'nroCte',
        'dataPrevisao',
        'distanciaParaDestinoEmMetros',
        'duracaoParaDestinoEmSegundos',
      ],
      CargaSessionAgcoPublic: [
        'nroNotaFiscal',
        'nroCte',
        'dataPrevisao',
        'distanciaParaDestinoEmMetros',
        'duracaoParaDestinoEmSegundos',
      ],
      CargaSession2: [
        'nroNotaFiscal',
        'danfe',
        'cte',
        'dataPrevisao',
        'distanciaParaDestinoEmMetros',
        'duracaoParaDestinoEmSegundos',
      ],
      CargaDestinatario: ['nome', 'cpfCnpj', 'telefone', 'endereco', 'email'],
      CargaResponsavel: ['nome', 'cnpj'],
      SacResponse: ['attempt', 'status', 'event', 'date', 'shipperName'],
      CargaSessionPublicCnhIveco: [
        'nroNotaFiscal',
        'nroCte',
        'dataPrevisao',
        'dataPrevisaoAtualizadaPublic',
        'observacaoPrevisaoPublic',
        'distanciaParaDestinoEmMetros',
        'duracaoParaDestinoEmSegundos',
      ],
      CargaSessionPublicCnhIveco2: [
        'slaStatusTracking',
      ],
      CargaSessionPublicCnhIveco2ShowSacPrivado: [
        'slaStatusTracking',
        'lastUpdate',
      ],
    },
    relatorioGeracaoList: {
      RelatorioGeracao: [
        'idRelatorioGeracaoPk',
        'dataCriacao',
        'dataInicio',
        'dataFim',
        'arquivoSolicitado',
        'dataGeracaoRelatorio',
        'urlDownload',
      ],
    },
    reportsAuditByDelivery: {
      AuditByDelivery: [
        'idCargaPk',
        'nroNotaFiscal',
        'dataCriacao',
        'dataBaixaSistema',
        'statusAuditoria',
        'statusNotificacaoAuditoria',
        'dataEnvio',
      ],
    },
    reportDeliveryClientNofification: {
        Carga: [
        'nroNotaFiscal',
        'notificacao',
        'observacaoEntrega',
        'dataCriacao',
        'nomeDestinatario',
        'nomeTransportador',
      ],
    },
    modalAudit: {
      Carga: [
        'nroNotaFiscal',
        'dataFinalizacao',
        'event',
        'nomeRecebedor',
        'documentoRecebedor',
        'rgRecebedor' ,
        'outroDocumentoRecebedor',
      ],
    },
  }

  /**
   * Blueprint of csv files
   */
  static readonly csvBlueprint = {
    queryNf: {
      nf: ['nf', 'NF', 'NOTA_FISCAL', 'Nota Fiscal'],
    },
    queryDriverAssociation: {
      idMotorista: ['ID ENTREGADOR', 'ID_ENTREGADOR', 'ID Entregador'],
      idCarga: ['ID CARGA', 'ID_CARGA', 'ID Carga'],
    },
    expectedDate: {
      idCargaPk: ['ID_CARGA'],
      dataPrevisao: ['DATA_PREVISAO_ENTREGA'],
      dataPrevisaoAtualizada: ['DATA_PREVISAO_ATUALIZADA'],
      observacaoPrevisao: ['DESCRICAO_MOTIVO_PREVISAO'],
    },
    orderNumber: {
      idCargaPk: ['ID_CARGA'],
      xpedido: ['NRO_PEDIDO'],
    },
    issueDate: {
      idCargaPk: ['ID_CARGA'],
      dataEmissaoDanfe: ['DATA_EMISSAO'],
    },
    emails: {
      cpfCnpj: ['CPF / CNPJ - DESTINATARIO', 'CPF / CNPJ - DESTINATÁRIO'],
      email: ['EMAIL - DESTINATARIO', 'EMAIL - DESTINATÁRIO'],
    },
    riskArea: {
      cepInicial: ['CEP INICIAL', 'CEP_INICIAL'],
      cepFinal: ['CEP FINAL', 'CEP_FINAL'],
      notificarNoApp: ['NOTIFICAR NO APP', 'NOTIFICAR_NO_APP'],
      notificarNoPainel: ['NOTIFICAR NO PAINEL', 'NOTIFICAR_NO_PAINEL'],
    },
    configNovaSla: {
      cnpjEmbarcador: ['CPF/CNPJ EMBARCADOR'],
      cnpjTransportador: ['CPF/CNPJ TRANSPORTADOR'],
      ufDestino: ['UF DESTINO'],
      cidadeDestino: ['CIDADE DESTINO'],
      sla: ['SLA'],
      seg: ['SEGUNDA'],
      ter: ['TERCA'],
      qua: ['QUARTA'],
      qui: ['QUINTA'],
      sex: ['SEXTA'],
      sab: ['SABADO'],
      dom: ['DOMINGO'],
    },
    contraSenhas: {
      cpfCnpj: ['CPF / CNPJ - DESTINATARIO', 'CPF / CNPJ - DESTINATÁRIO'],
      contraSenha: ['CEL - DESTINATARIO', 'CEL - DESTINÁTARIO'],
    },
    emailAlertaNotaAtraso: {
      cpfCnpj: ['CPF / CNPJ - TRANSPORTADOR', 'CPF / CNPJ - TRANSPORTADOR'],
      email: ['EMAIL - TRANSPORTADOR', 'EMAIL - TRANSPORTADOR'],
    },
  }

  /*
   * Blueprint of txt files
   */
  static readonly txtBlueprint = {
    issueDate: {
      nroNotaFiscal: ['N. Nota Fiscal'],
      xpedido: ['Pedido'],
      dataEmissaoDanfe: ['Dat Docdat'],
      cpfCnpj: ['CNPJ'],
    },
  }
}
